export type PendingPaymentContextType = 'appointment' | 'payment' | 'prescription' | 'product';

export type PendingPayment = {
  startedAt: number;
  sourcePath: string;
  novalnetUrl: string;
  contextType: PendingPaymentContextType;
  contextId?: number | string;
};

const PENDING_PAYMENT_STORAGE_KEY = 'pendingExternalPayment';
export const PENDING_PAYMENT_TTL_MS = 30 * 60 * 1000;

export const setPendingPayment = (pending: PendingPayment) => {
  localStorage.setItem(PENDING_PAYMENT_STORAGE_KEY, JSON.stringify(pending));
};

export const clearPendingPayment = () => {
  localStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY);
};

export const getPendingPayment = (): PendingPayment | null => {
  try {
    const value = localStorage.getItem(PENDING_PAYMENT_STORAGE_KEY);
    if (!value) {
      return null;
    }

    const parsed = JSON.parse(value) as PendingPayment;
    if (
      !parsed ||
      typeof parsed.startedAt !== 'number' ||
      typeof parsed.sourcePath !== 'string' ||
      typeof parsed.novalnetUrl !== 'string' ||
      typeof parsed.contextType !== 'string'
    ) {
      clearPendingPayment();

      return null;
    }

    return parsed;
  } catch (error) {
    clearPendingPayment();

    return null;
  }
};

export const isPendingPaymentFresh = (pending: PendingPayment | null): pending is PendingPayment => {
  if (!pending) {
    return false;
  }

  return Date.now() - pending.startedAt < PENDING_PAYMENT_TTL_MS;
};

export const getFreshPendingPayment = (): PendingPayment | null => {
  const pending = getPendingPayment();
  if (!isPendingPaymentFresh(pending)) {
    clearPendingPayment();

    return null;
  }

  return pending;
};

export const hasKnownPaymentRedirectParams = (search: string): boolean => {
  if (!search) {
    return false;
  }

  const params = new URLSearchParams(search);
  if (params.has('status') || params.has('paymentId') || params.has('payment_type') || params.has('status_text')) {
    return true;
  }

  const normalized = search.toLowerCase();

  return (
    normalized.includes('status=') ||
    normalized.includes('paymentid=') ||
    normalized.includes('payment_type=') ||
    normalized.includes('status_text=')
  );
};
